import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CreditCard, Mail, MapPin, Pencil, Phone, Plus, Trash2, UserCircle } from 'lucide-react';

import { errorMessage } from '@/api/client';
import { customersApi } from '@/api/resources';
import { EmptyState, Loading, PageHeader, SubmitButton } from '@/components/common';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const EMPTY = { name: '', phone: '', email: '', address: '', ci_ruc: '', notes: '' };

export default function Clientes() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(async () => {
    try {
      setCustomers(await customersApi.list());
    } catch (error) {
      toast.error(errorMessage(error, 'No se pudieron cargar los clientes'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setDialogOpen(true);
  };

  const openEdit = (customer) => {
    setEditing(customer);
    setForm({
      name: customer.name || '',
      phone: customer.phone || '',
      email: customer.email || '',
      address: customer.address || '',
      ci_ruc: customer.ci_ruc || '',
      notes: customer.notes || '',
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }

    setSaving(true);
    // El backend rechaza cadenas vacías donde espera null
    const payload = Object.fromEntries(
      Object.entries(form).map(([key, value]) => [key, value.trim() || null])
    );

    try {
      if (editing) {
        await customersApi.update(editing.id, payload);
        toast.success('Cliente actualizado');
      } else {
        await customersApi.create(payload);
        toast.success('Cliente creado');
      }
      setDialogOpen(false);
      load();
    } catch (error) {
      toast.error(errorMessage(error, 'No se pudo guardar el cliente'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (customer) => {
    if (!window.confirm(`¿Eliminar a ${customer.name}?`)) return;
    try {
      await customersApi.remove(customer.id);
      toast.success('Cliente eliminado');
      load();
    } catch (error) {
      toast.error(errorMessage(error, 'No se pudo eliminar el cliente'));
    }
  };

  const term = search.trim().toLowerCase();
  const visible = term
    ? customers.filter(
        (c) =>
          c.name.toLowerCase().includes(term) ||
          (c.phone || '').toLowerCase().includes(term) ||
          (c.ci_ruc || '').toLowerCase().includes(term)
      )
    : customers;

  return (
    <div className="space-y-6">
      <PageHeader title="Clientes" subtitle={`${customers.length} cliente(s) registrado(s)`}>
        <Button onClick={openNew} className="rounded-sm">
          <Plus size={18} className="mr-2" />
          Nuevo cliente
        </Button>
      </PageHeader>

      <Input
        placeholder="Buscar por nombre, teléfono o CI/RUC…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="rounded-sm"
      />

      {loading ? (
        <Loading />
      ) : visible.length === 0 ? (
        <EmptyState
          icon={UserCircle}
          title={term ? 'Sin resultados' : 'Todavía no hay clientes'}
          description={
            term ? 'Probá con otro término de búsqueda.' : 'Cargá tu primer cliente para empezar.'
          }
          action={
            !term && (
              <Button onClick={openNew} className="rounded-sm">
                <Plus size={18} className="mr-2" />
                Nuevo cliente
              </Button>
            )
          }
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {visible.map((customer) => (
            <div key={customer.id} className="rounded-sm border border-border bg-card p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary font-bold text-primary-foreground">
                    {customer.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="truncate font-bold">{customer.name}</p>
                    {customer.ci_ruc && (
                      <p className="flex items-center gap-1 truncate text-xs text-muted-foreground">
                        <CreditCard size={11} />
                        {customer.ci_ruc}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex shrink-0">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(customer)} aria-label="Editar">
                    <Pencil size={15} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(customer)}
                    aria-label="Eliminar"
                  >
                    <Trash2 size={15} className="text-red-500" />
                  </Button>
                </div>
              </div>

              <div className="mt-3 space-y-1 text-sm text-muted-foreground">
                {customer.phone && (
                  <a
                    href={`tel:${customer.phone}`}
                    className="flex items-center gap-2 hover:text-foreground"
                  >
                    <Phone size={13} />
                    {customer.phone}
                  </a>
                )}
                {customer.email && (
                  <p className="flex items-center gap-2 truncate">
                    <Mail size={13} />
                    {customer.email}
                  </p>
                )}
                {customer.address && (
                  <p className="flex items-center gap-2 truncate">
                    <MapPin size={13} />
                    {customer.address}
                  </p>
                )}
                {customer.notes && (
                  <p className="pt-1 text-xs italic">{customer.notes}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar cliente' : 'Nuevo cliente'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nombre *</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                className="rounded-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="phone">Teléfono</Label>
                <Input
                  id="phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ci_ruc">CI / RUC</Label>
                <Input
                  id="ci_ruc"
                  value={form.ci_ruc}
                  onChange={(e) => setForm({ ...form, ci_ruc: e.target.value })}
                  className="rounded-sm"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="rounded-sm"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="address">Dirección</Label>
              <Input
                id="address"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                className="rounded-sm"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Notas</Label>
              <Textarea
                id="notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3}
                className="rounded-sm"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                className="rounded-sm"
                onClick={() => setDialogOpen(false)}
              >
                Cancelar
              </Button>
              <SubmitButton loading={saving}>{editing ? 'Guardar' : 'Crear'}</SubmitButton>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
